import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient } from '../../config/api';
import { AdminApiResponse } from '../../types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Chat,
  Message,
  TrendingUp,
  Visibility,
  Search,
  Group,
  Refresh,
  Close,
} from '@mui/icons-material';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConversationUserInfo {
  userId: number;
  name: string;
  profileImageUrl?: string | null;
}

interface ConversationDto {
  conversationId: number;
  user1: ConversationUserInfo;
  user2: ConversationUserInfo;
  lastMessageContent: string | null;
  messageCount: number;
  cupidCardDeliveryId: number;
  createdTime: string;
}

interface ConversationPageDto {
  content: ConversationDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface MessageDto {
  messageId: number;
  senderUserId: number;
  senderName: string;
  content: string;
  isRead: boolean;
  readAt: string | null;
  createdTime: string;
}

interface MessagePageDto {
  content: MessageDto[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return `${Math.floor(hr / 24)}일 전`;
}

function formatDateTime(isoStr: string): string {
  return new Date(isoStr).toLocaleString('ko-KR', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const AVATAR_COLORS = ['#EC4899', '#FF6B35', '#8B5CF6', '#0EA5E9', '#10B981'];

function avatarColor(userId: number): string {
  return AVATAR_COLORS[userId % AVATAR_COLORS.length];
}

// ── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  accentColor: string;
  bgColor: string;
}

function StatCard({ icon, value, label, accentColor, bgColor }: StatCardProps) {
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid #F0F0F0',
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: '12px',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: `0 8px 24px ${accentColor}22`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2.5, bgcolor: bgColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
          {icon}
        </Box>
        <Typography variant="h4" fontWeight={800} color="text.primary" lineHeight={1}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const AdminChatsPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Conversations state
  const [conversations, setConversations]   = useState<ConversationDto[]>([]);
  const [totalElements, setTotalElements]   = useState(0);
  const [loadingConvs, setLoadingConvs]     = useState(false);

  // Message dialog state
  const [selectedConv, setSelectedConv]     = useState<ConversationDto | null>(null);
  const [messages, setMessages]             = useState<MessageDto[]>([]);
  const [loadingMsgs, setLoadingMsgs]       = useState(false);
  const [dialogOpen, setDialogOpen]         = useState(false);

  // Auth guard
  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  // ── Fetch conversations ──────────────────────────────────────────────────
  const fetchConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const data: AdminApiResponse<ConversationPageDto> = await apiClient.get(
        '/api-admin/v1/gth-common/silver-connect/conversations?page=0&size=20'
      );
      if (data.resultType === 'SUCCESS' && data.result) {
        setConversations(data.result.content);
        setTotalElements(data.result.totalElements);
      }
    } catch (err) {
      console.error('채팅방 목록 로드 실패:', err);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // ── Open message dialog ──────────────────────────────────────────────────
  const openMessages = async (conv: ConversationDto) => {
    setSelectedConv(conv);
    setDialogOpen(true);
    setLoadingMsgs(true);
    setMessages([]);
    try {
      const data: AdminApiResponse<MessagePageDto> = await apiClient.get(
        `/api-admin/v1/gth-common/silver-connect/conversations/${conv.conversationId}/messages?page=0&size=50`
      );
      if (data.resultType === 'SUCCESS' && data.result) {
        setMessages(data.result.content);
      }
    } catch (err) {
      console.error('메시지 로드 실패:', err);
    } finally {
      setLoadingMsgs(false);
    }
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  // Client-side search filter
  const filtered = conversations.filter((conv) => {
    if (!search.trim()) return true;
    const q = search.trim();
    return (
      conv.user1.name.includes(q) ||
      conv.user2.name.includes(q) ||
      (conv.lastMessageContent ?? '').includes(q)
    );
  });

  const totalMessages = conversations.reduce((sum, c) => sum + c.messageCount, 0);

  return (
    <AdminLayout>
      <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#FAFAFA' }}>

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  background: 'linear-gradient(135deg, #EC4899, #FF6B35)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                  mb: 0.5,
                }}
              >
                채팅방 관리
              </Typography>
              <Typography variant="body2" color="text.secondary">
                회원간 채팅 현황을 모니터링합니다
              </Typography>
            </Box>
            <Tooltip title="새로고침">
              <IconButton
                onClick={fetchConversations}
                disabled={loadingConvs}
                sx={{
                  border: '1px solid #F0E8F5', borderRadius: '12px',
                  color: '#888', '&:hover': { color: '#EC4899', background: '#FDF2F8' },
                }}
              >
                {loadingConvs
                  ? <CircularProgress size={18} sx={{ color: '#EC4899' }} />
                  : <Refresh sx={{ fontSize: 20 }} />
                }
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mt: 2, height: 3, width: 64, borderRadius: 2, background: 'linear-gradient(90deg, #EC4899, #FF6B35)' }} />
        </Box>

        {/* ── Stat Cards ──────────────────────────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
          <StatCard
            icon={<Chat sx={{ color: '#EC4899', fontSize: 22 }} />}
            value={totalElements}
            label="전체 채팅방"
            accentColor="#EC4899"
            bgColor="#FDF2F8"
          />
          <StatCard
            icon={<TrendingUp sx={{ color: '#22C55E', fontSize: 22 }} />}
            value={conversations.length}
            label="조회된 채팅방"
            accentColor="#22C55E"
            bgColor="#F0FDF4"
          />
          <StatCard
            icon={<Message sx={{ color: '#0EA5E9', fontSize: 22 }} />}
            value={totalMessages}
            label="총 메시지 수"
            accentColor="#0EA5E9"
            bgColor="#F0F9FF"
          />
          <StatCard
            icon={<Group sx={{ color: '#FF6B35', fontSize: 22 }} />}
            value={filtered.length}
            label="검색 결과"
            accentColor="#FF6B35"
            bgColor="#FFF8F3"
          />
        </Box>

        {/* ── Chat List Card ───────────────────────────────────────────── */}
        <Card elevation={0} sx={{ border: '1px solid #F0F0F0', borderRadius: '16px', overflow: 'hidden' }}>
          {/* Card header */}
          <Box sx={{
            px: 3, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #F5F5F5', bgcolor: 'white',
          }}>
            <Typography variant="h6" fontWeight={700}>채팅방 현황</Typography>
            <Typography variant="caption" color="text.disabled">
              {totalElements > 0 ? `전체 ${totalElements}개` : ''}
            </Typography>
          </Box>

          {/* Filter row */}
          <Box sx={{
            px: 3, py: 2, bgcolor: '#FAFAFA', borderBottom: '1px solid #F0F0F0',
            display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
          }}>
            <TextField
              size="small"
              placeholder="참여자 이름 또는 메시지 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 260,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white', borderRadius: 2,
                  '&.Mui-focused fieldset': { borderColor: '#EC4899' },
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {filtered.length}개 채팅방
            </Typography>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} elevation={0} sx={{ bgcolor: 'white' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'linear-gradient(90deg, #1A0A2E 0%, #4A1265 100%)' }}>
                  {['ID', '참여자', '최근 메시지', '메시지 수', '생성일', '액션'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, py: 1.5, whiteSpace: 'nowrap' }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingConvs && conversations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={32} sx={{ color: '#EC4899' }} />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.disabled' }}>
                      {search ? '검색 결과가 없습니다.' : '채팅방이 없습니다.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((conv) => (
                    <TableRow
                      key={conv.conversationId}
                      hover
                      sx={{
                        bgcolor: 'white',
                        '&:hover': { bgcolor: '#FAF5FF' },
                        transition: 'background-color 0.15s',
                        '& td': { borderBottom: '1px solid #F3EBF8' },
                      }}
                    >
                      {/* ID */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          #{conv.conversationId}
                        </Typography>
                      </TableCell>

                      {/* 참여자 */}
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {[conv.user1, conv.user2].map((u, i) => (
                            <React.Fragment key={u.userId}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Avatar sx={{
                                  width: 28, height: 28, fontSize: 12, fontWeight: 700,
                                  bgcolor: avatarColor(u.userId),
                                }}>
                                  {u.name.slice(-1)}
                                </Avatar>
                                <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                              </Stack>
                              {i === 0 && (
                                <Typography variant="caption" color="text.disabled" sx={{ px: 0.25 }}>
                                  ↔
                                </Typography>
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </TableCell>

                      {/* 최근 메시지 */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" color="text.primary" noWrap>
                          {conv.lastMessageContent ?? '–'}
                        </Typography>
                      </TableCell>

                      {/* 메시지 수 */}
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {conv.messageCount}
                        </Typography>
                      </TableCell>

                      {/* 생성일 */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                          {formatRelativeTime(conv.createdTime)}
                        </Typography>
                      </TableCell>

                      {/* 액션 */}
                      <TableCell align="center">
                        <Tooltip title="메시지 보기" placement="top">
                          <IconButton
                            size="small"
                            onClick={() => openMessages(conv)}
                            sx={{ color: '#EC4899', '&:hover': { bgcolor: '#FDF2F8' } }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* ── Message Dialog ───────────────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #1A0A2E, #4A1265)',
            p: 3, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box>
              <Typography fontWeight={800} fontSize="1.05rem">채팅 내역</Typography>
              {selectedConv && (
                <Typography fontSize="0.78rem" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>
                  {selectedConv.user1.name} ↔ {selectedConv.user2.name} · {selectedConv.messageCount}개 메시지
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setDialogOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, maxHeight: 480, overflowY: 'auto' }}>
          {loadingMsgs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#EC4899' }} />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: '#AAA' }}>
              <Typography>메시지가 없습니다.</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map((msg) => (
                <Box key={msg.messageId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Avatar sx={{
                      width: 24, height: 24, fontSize: 11, fontWeight: 700,
                      bgcolor: avatarColor(msg.senderUserId),
                    }}>
                      {msg.senderName.slice(-1)}
                    </Avatar>
                    <Typography fontSize="0.78rem" fontWeight={700} color="#555">
                      {msg.senderName}
                    </Typography>
                    <Typography fontSize="0.7rem" color="#BBB">
                      {formatDateTime(msg.createdTime)}
                    </Typography>
                    {msg.isRead && (
                      <Typography fontSize="0.65rem" color="#22C55E" fontWeight={700}>읽음</Typography>
                    )}
                  </Box>
                  <Box sx={{
                    ml: 4, px: 1.5, py: 1, borderRadius: '10px',
                    background: '#F8F4F9', border: '1px solid #EDE0F5',
                  }}>
                    <Typography fontSize="0.85rem" color="#333">{msg.content}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminChatsPage;
